import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
	MortgagePriorityDescription,
	getLenderLogoUrlByMortgage,
} from '@core/util/mortgage';
import {
	MatterMortgage,
	MatterMortgageType,
	MatterMortgageWithAssociations,
	MortgageStep,
	TransactionType,
} from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgageDispatch } from '@state/matter/matter_mortgages';
import { MatterMortgagesState } from '@state/matter/matter_mortgages/matter-mortgages.state';
import { Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { AddMortgageComponent } from './add-mortgage/add-mortgage.component';
import { LlcRepoService } from '@repos/mortgages/llc.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { NavigationService } from '@services/nav/nav.service';
import { CloserSyncRepoService } from '@repos/mortgages/closer-sync/closer-sync.service';
import { MatterFileType } from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { StatementOfAccountsDispatch } from '@state/matter/statementOfAccounts/statementOfAccounts.dispatch';
import {
	StatementOfAccountDefaultTypes,
	TERAVIEW_FEE_TYPES,
} from '@models/statementOfAccount';

interface MortgageTab {
	id: string;
	relatedMortgageId: string;
	type: MatterMortgageType;
	transactionType: TransactionType;
	description: string;
	logoUrl?: string;
}

@UntilDestroy()
@Component({
	selector: 'app-mortgages',
	templateUrl: './mortgages.component.html',
	styleUrls: ['./mortgages.component.scss'],
	standalone: false,
})
export class MatterMortgagesComponent implements OnInit {
	public ICON = ICON;
	public tabs: MortgageTab[] = [];
	public canAdd = true;

	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(MatterMortgagesState.getAllMortgagesWithAssociations)
	mortgages$: Observable<MatterMortgageWithAssociations[]>;
	@Select(SelectedMatter.matterFileType) fileType$: Observable<MatterFileType>;
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	selectedMortgage: MatterMortgage;
	mortgages: MatterMortgage[];
	deletingTab: string = null;

	fileTypes = MatterFileType;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private dialog: MatDialog,
		private matterMortgageDispatch: MatterMortgageDispatch,
		private statementOfAccountsDispatch: StatementOfAccountsDispatch,
		private llcService: LlcRepoService,
		private snackbarService: SnackbarService,
		private navigationService: NavigationService,
		private closerSyncRepoService: CloserSyncRepoService,
	) {}

	ngOnInit(): void {
		this.initializeSelectedMortgage();
		this.subscribeToRouterEvents();
		this.subscribeToMortgagesChanges();
	}

	private subscribeToMortgagesChanges(): void {
		this.mortgages$.pipe(untilDestroyed(this)).subscribe((mortgages) => {
			this.mortgages = mortgages;
			this.canAdd = this.mortgages?.length < 5;

			this.tabs = mortgages.map((_m) => ({
				id: _m.id,
				type: _m.type,
				relatedMortgageId: _m.relatedMortgageId,
				description: this.getMortgageTabName(_m),
				transactionType: _m.transactionType,
				url: this.getUrl(_m),
				logoUrl: getLenderLogoUrlByMortgage(_m),
			}));

			if (this.checkForRedirect()) {
				this.selectedMortgageRedirectHandler();
			}

			if (mortgages.length === 0 && this.fileType === MatterFileType.SALE) {
				this.navigationService.goToMatter_APS(this.matterId);
			}
		});
	}

	private subscribeToRouterEvents(): void {
		this.router?.events.pipe(untilDestroyed(this)).subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.handleRouterNavigation();
			}
		});
	}

	private handleRouterNavigation(): void {
		const mortgageId = this.route?.firstChild?.snapshot?.params?.mortgageId;
		if (mortgageId) {
			this.matterMortgageDispatch.setSelectedMortgage(mortgageId);
		} else if (this.mortgages?.length > 0) {
			this.selectedMortgageRedirectHandler();
		}
	}

	private selectedMortgageRedirectHandler(): void {
		if (this.mortgages[0].step !== MortgageStep.INTAKE) {
			this.navigationService.goToMatter_Mortgages_Mortgage(
				this.matterId,
				this.mortgages[0].id,
			);
		} else if (this.mortgages[0].type === MatterMortgageType.LLC) {
			this.navigationService.goToMatter_Mortgages_Mortgage_AssignSync(
				this.matterId,
				this.mortgages[0].id,
			);
		} else {
			this.navigationService.goToMatter_Mortgages_Mortgage_Intake(
				this.matterId,
				this.mortgages[0].id,
			);
		}
	}

	private initializeSelectedMortgage(): void {
		const mortgageId = this.route?.firstChild?.snapshot?.params?.mortgageId;
		if (mortgageId) {
			this.matterMortgageDispatch.setSelectedMortgage(mortgageId);
		}
	}

	// This method is for redirecting sync mortgages to the intake forms, there was a bug where it would skip the intake
	// based on the number of mortgages, wo we check to make sure there's atleast 1, and that there is no mortgage id in
	// the url.
	private checkForRedirect(): boolean {
		return (
			!this.route?.firstChild?.snapshot?.params?.mortgageId &&
			this.mortgages?.length > 0
		);
	}

	getMortgageTabName(mortgage: MatterMortgageWithAssociations): string {
		if (mortgage?.transactionType === TransactionType.BRIDGE) {
			return '[Bridge]';
		}
		return MortgagePriorityDescription[mortgage?.priority];
	}
	getUrl = (mortgage: MatterMortgage) =>
		UrlService.Matter_Mortgages_Mortgage(this.matterId, mortgage.id);

	addMortgage = () => {
		this.dialog.open(AddMortgageComponent, {
			maxWidth: '550px',
			width: '90%',
		});
	};

	private deleteStatementOfAccounts(mortgageId: string, types: string[]): void {
		types.forEach((type) => {
			// TODO: remove defaultType after the id once script is run
			this.statementOfAccountsDispatch.Delete(
				`${type}-${mortgageId}-${StatementOfAccountDefaultTypes.REG_MORTGAGE}`,
			);
		});
	}

	remove = async (tab: MortgageTab) => {
		this.deletingTab = tab.id;

		try {
			if (tab.type === MatterMortgageType.MANUAL) {
				this.matterMortgageDispatch.remove(tab.id);
			} else if (tab.type === MatterMortgageType.LLC) {
				// find all mortgages with the same llc relatedMortgageId incase duplicate mortgages are shown in the matter
				// this has happened on occasion due to the user double accepting a mortgage
				if (tab.transactionType === TransactionType.BRIDGE) {
					this.snackbarService.openSnackBar({
						text: 'Please remove the primary mortgage to remove the bridge',
						type: 'info',
					});

					return;
				}

				await this.llcService.deleteLLCMatterMortgage({
					matterId: this.matterId,
					relatedMortgageId: tab.relatedMortgageId,
				});
			} else if (tab.type === MatterMortgageType.CLOSER_SYNC) {
				await this.closerSyncRepoService.deleteMatterMortgage(
					tab.id,
					this.matterId,
					tab.relatedMortgageId,
				);
			}

			this.deleteStatementOfAccounts(tab.id, TERAVIEW_FEE_TYPES);

			// redirect to new mortgage
			const mortgageIds = this.mortgages
				.filter((x) => x.id !== tab.id)
				.map((x) => x.id);
			if (mortgageIds.length > 0) {
				this.navigationService.goToMatter_Mortgages_Mortgage(
					this.matterId,
					this.mortgages[0].id,
				);
			}
		} catch (err) {
			console.log('Error deleting tab', tab, err);
		} finally {
			this.deletingTab = null;
		}
	};
}
